"use client";

import React, { useState, useRef, useEffect } from "react";
import { Loader2, Upload, X, Sparkles, Image as ImageIcon, ChevronDown, AlertCircle } from "lucide-react";
import { Listbox, Transition } from "@headlessui/react";
import type { PluginMeta, PluginInputField } from "@/lib/processors/base";
import { Group } from "@/types/group.interface";
import { Project } from "@/types/project.interface";
import { AutoSegmentReviewPanel } from "./AutoSegmentReviewPanel";
import { ImageLightbox } from "./ImageLightbox";
import { ImageSlot } from "./ImageSlot";

type PipelineUIState = 'form' | 'submitting' | 'processing' | 'review' | 'generating' | 'video_done' | 'error';

interface AutoWorkflowPanelProps {
    group: Group;
    plugin: PluginMeta;
    project?: Project;
    onPipelineStarted: () => void;
    isGenerating: boolean;
}

type FormValues = Record<string, string | number | string[]>;

function getDefaults(schema: PluginInputField[], savedInputs?: Record<string, any> | null): FormValues {
    const defaults: FormValues = {};
    for (const field of schema) {
        if (savedInputs && savedInputs[field.key] !== undefined) {
            defaults[field.key] = savedInputs[field.key];
        } else if (field.defaultValue !== undefined) {
            defaults[field.key] = field.defaultValue;
        } else if (field.type === "images") {
            defaults[field.key] = [];
        } else if (field.type === "number") {
            defaults[field.key] = field.min ?? 1;
        } else {
            defaults[field.key] = "";
        }
    }
    return defaults;
}

export const AutoWorkflowPanel: React.FC<AutoWorkflowPanelProps> = ({
    group,
    plugin,
    project,
    onPipelineStarted,
    isGenerating,
}) => {
    const schema = plugin.inputSchema ?? [];
    const savedInputs = group.workflowInputs;

    // 初始化表单和预览态 (Phase 53)
    const initialForm = getDefaults(schema, savedInputs);
    const initialPreviews: Record<string, string[]> = {};
    // 将保存的图片 key 直接转为预览路径（S3 签名 URL 等加载会在渲染时处理）
    if (savedInputs && savedInputs.productImages && Array.isArray(savedInputs.productImages)) {
        initialPreviews.productImages = savedInputs.productImages.map((k: string) => `/api/files/signed?key=${encodeURIComponent(k)}`);
    }

    // 结合后端最新的 status 做初态推导，实现"缓存"留存
    const getInitialUiState = (): PipelineUIState => {
        if (group.status === "review" || (group.status as string) === "pre_gen_done") return "review";
        if (group.status === "pre_gen") return "processing";
        if (group.status === "generating") return "generating";
        if (group.status === "completed") return "video_done";
        return "form";
    };

    const [form, setForm] = useState<FormValues>(initialForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);
    const [previews, setPreviews] = useState<Record<string, string[]>>(initialPreviews);
    const [uiState, setUiState] = useState<PipelineUIState>(getInitialUiState());
    const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
    const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const sseRef = useRef<EventSource | null>(null);

    // Poll pipeline stage via SSE
    const startPolling = () => {
        if (sseRef.current) sseRef.current.close();
        const es = new EventSource('/api/events');
        sseRef.current = es;
        es.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data);
                const stage = data.pipelineStage as string;
                if (stage === 'review') {
                    setUiState('review');
                    es.close();
                } else if (stage === 'generating') {
                    setUiState('generating');
                } else if (stage === 'done') {
                    setUiState('video_done');
                    onPipelineStarted();
                    es.close();
                } else if (stage === 'error') {
                    setError(data.error || '流水线出错');
                    setUiState('error');
                    es.close();
                }
            } catch { }
        };
        es.onerror = () => {
            es.close();
        };
    };

    // Cleanup SSE on unmount
    useEffect(() => () => { sseRef.current?.close(); }, []);

    // If parent says isGenerating (Veo started from review panel), show that state
    useEffect(() => {
        if (isGenerating && uiState === 'review') {
            setUiState('generating');
            startPolling();
        }
    }, [isGenerating]);


    const setValue = (key: string, value: string | number | string[]) => {
        setForm(prev => ({ ...prev, [key]: value }));
    };

    const handleImageUpload = async (key: string, files: FileList | null) => {
        if (!files || files.length === 0) return;
        setUploadingKey(key);
        const existing = (form[key] as string[]) ?? [];
        const field = schema.find(f => f.key === key);
        const maxFiles = field?.maxFiles ?? 5;

        const newKeys: string[] = [];
        const newPreviews: string[] = [];

        for (let i = 0; i < Math.min(files.length, maxFiles - existing.length); i++) {
            const file = files[i];
            const fd = new FormData();
            fd.append("file", file);
            try {
                const res = await fetch("/api/upload/product", { method: "POST", body: fd });
                if (res.ok) {
                    const data = await res.json();
                    // Store the S3 key for the pipeline; use signed URL for preview
                    newKeys.push(data.key);
                    newPreviews.push(data.url); // for display only
                }
            } catch (e) {
                console.error("图片上传失败:", e);
            }
        }

        setValue(key, [...existing, ...newKeys]);
        // Store signed URLs for preview separately under a _preview suffix
        setPreviews(prev => ({
            ...prev,
            [key]: [...(prev[key] ?? []), ...newPreviews],
        }));
        setUploadingKey(null);
    };

    const removeImage = (key: string, index: number) => {
        const arr = [...((form[key] as string[]) ?? [])];
        arr.splice(index, 1);
        setValue(key, arr);
    };

    const handleSubmit = async () => {
        setError(null);
        for (const field of schema) {
            if (!field.required) continue;
            const val = form[field.key];
            if (field.type === "images" && (val as string[]).length === 0) {
                setError(`请上传至少一张${field.label}`);
                return;
            }
            if (field.type !== "images" && !String(val ?? "").trim()) {
                setError(`请填写「${field.label}」`);
                return;
            }
        }

        setIsSubmitting(true);
        setUiState('submitting');
        try {
            const res = await fetch("/api/pipeline/run", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ groupId: group.id, provider: plugin.id, inputs: form }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "启动流水线失败");
            setUiState('processing');
            startPolling();
        } catch (e: any) {
            setError(e.message);
            setUiState('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── State machine rendering ─────────────────────────────────────────────

    if (uiState === 'review' || uiState === 'generating' || uiState === 'video_done') {
        return (
            <AutoSegmentReviewPanel
                group={group}
                plugin={plugin}
                originalInputs={form}
                imagenPreviewUrls={previews.productImages ?? []}
                isGenerating={uiState === 'generating' || isGenerating}
                onVideoStarted={() => { setUiState('generating'); startPolling(); onPipelineStarted(); }}
                onReset={() => { setUiState('form'); setError(null); }}
            />
        );
    }

    if (uiState === 'processing' || uiState === 'submitting') {
        const stageLabel = uiState === 'submitting'
            ? '提交中...'
            : 'Imagen + Gemini 生成中...';
        const stageDesc = uiState === 'processing'
            ? '正在处理商品图并自动生成提案词，完成后将显示审查界面'
            : '';
        return (
            <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 px-8 py-16 flex flex-col items-center gap-4">
                <div className="relative">
                    <div className="w-12 h-12 rounded-full border-2 border-purple-500/20 border-t-purple-500 animate-spin" />
                    <Sparkles size={18} className="absolute inset-0 m-auto text-purple-500" />
                </div>
                <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{stageLabel}</p>
                {stageDesc && <p className="text-xs text-zinc-400 dark:text-zinc-500 text-center max-w-xs">{stageDesc}</p>}
                <div className="flex items-center gap-1.5 text-[9px] text-zinc-400 mt-2">
                    {(plugin.pipeline ?? []).map((stage, i) => (
                        <span key={stage} className="flex items-center gap-1.5">
                            <span className={`px-2 py-0.5 rounded font-mono uppercase ${(uiState === 'processing' && (stage === 'imagen' || stage === 'gemini'))
                                ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400'
                                : 'bg-zinc-100 dark:bg-zinc-800'
                                }`}>{stage}</span>
                            {i < (plugin.pipeline?.length ?? 0) - 1 && <span>→</span>}
                        </span>
                    ))}
                </div>
            </div>
        );
    }

    const renderField = (field: PluginInputField) => {
        switch (field.type) {
            case "images": {
                const images = (form[field.key] as string[]) ?? [];
                const maxFiles = field.maxFiles ?? 5;
                // Show filled slots + one empty slot (if under max)
                const slots = [...images, ...(images.length < maxFiles ? [undefined] : [])];
                return (
                    <div key={field.key} className="space-y-2">
                        <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400 block">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <p className="text-[10px] text-zinc-400 dark:text-zinc-600">{field.description}</p>
                        <div className="flex flex-wrap gap-2 relative">
                            {slots.map((imgUrl, idx) => (
                                <ImageSlot
                                    key={idx}
                                    src={imgUrl}
                                    accept={field.accept ?? "image/*"}
                                    onAdd={(url) => {
                                        const prev = (form[field.key] as string[]) ?? [];
                                        if (idx < prev.length) {
                                            // Replace existing
                                            const next = [...prev];
                                            next[idx] = url;
                                            setValue(field.key, next);
                                            setPreviews(p => { const a = [...(p[field.key] ?? [])]; a[idx] = url; return { ...p, [field.key]: a }; });
                                        } else {
                                            // Append new
                                            setValue(field.key, [...prev, url]);
                                            setPreviews(p => ({ ...p, [field.key]: [...(p[field.key] ?? []), url] }));
                                        }
                                    }}
                                    onRemove={() => {
                                        removeImage(field.key, idx);
                                        setPreviews(prev => {
                                            const arr = [...(prev[field.key] ?? [])];
                                            arr.splice(idx, 1);
                                            return { ...prev, [field.key]: arr };
                                        });
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                );
            }
            case "text":
                return (
                    <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400 block">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="text"
                            value={String(form[field.key] ?? "")}
                            onChange={e => setValue(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors placeholder:text-zinc-400"
                        />
                        {field.description && <p className="text-[10px] text-zinc-400 dark:text-zinc-600">{field.description}</p>}
                    </div>
                );
            case "textarea":
                return (
                    <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400 block">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <textarea
                            value={String(form[field.key] ?? "")}
                            onChange={e => setValue(field.key, e.target.value)}
                            placeholder={field.placeholder}
                            rows={4}
                            className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors placeholder:text-zinc-400 resize-none"
                        />
                        {field.description && <p className="text-[10px] text-zinc-400 dark:text-zinc-600">{field.description}</p>}
                    </div>
                );
            case "select": {
                const options = field.options ?? [];
                const currentVal = String(form[field.key] ?? field.defaultValue ?? "");
                const selectedOpt = options.find(o => o.value === currentVal) ?? options[0];
                return (
                    <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400 block">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <Listbox value={currentVal} onChange={v => setValue(field.key, v)}>
                            <div className="relative">
                                <Listbox.Button className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm text-left flex justify-between items-center outline-none focus:border-blue-500 transition-colors text-zinc-700 dark:text-zinc-300">
                                    <span>{selectedOpt?.label}</span>
                                    <ChevronDown size={14} className="text-zinc-400 shrink-0" />
                                </Listbox.Button>
                                <Transition enter="transition ease-out duration-100" enterFrom="opacity-0 -translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition ease-in duration-75" leaveFrom="opacity-100" leaveTo="opacity-0">
                                    <Listbox.Options className="absolute z-20 w-full mt-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 outline-none">
                                        {options.map(opt => (
                                            <Listbox.Option key={opt.value} value={opt.value}
                                                className={({ active }) => `px-3 py-2 text-sm cursor-pointer transition-colors text-zinc-800 dark:text-zinc-200 ${active ? 'bg-zinc-100 dark:bg-zinc-800' : ''}`}
                                            >
                                                {opt.label}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        </Listbox>
                        {field.description && <p className="text-[10px] text-zinc-400 dark:text-zinc-600">{field.description}</p>}
                    </div>
                );
            }
            case "number":
                return (
                    <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-zinc-500 dark:text-zinc-400 block">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <input
                            type="number"
                            value={Number(form[field.key] ?? field.defaultValue ?? 1)}
                            onChange={e => setValue(field.key, Number(e.target.value))}
                            min={field.min}
                            max={field.max}
                            className="w-32 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 outline-none focus:border-blue-500 transition-colors"
                        />
                        {field.description && <p className="text-[10px] text-zinc-400 dark:text-zinc-600">{field.description}</p>}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="bg-white dark:bg-zinc-900/60 border border-zinc-200 dark:border-zinc-800 rounded-none">
                {/* Header */}
                <div className="px-6 py-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                        <Sparkles size={16} className="text-purple-500 dark:text-purple-400" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">自动化视频生成</h3>
                        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 mt-0.5">
                            提供商品信息 → AI 自动生成素材图 & 提示词 → Veo 视频
                        </p>
                    </div>
                    {/* Pipeline 流程图示 */}
                    <div className="ml-auto flex items-center gap-1.5 text-[9px] text-zinc-400">
                        {(plugin.pipeline ?? []).map((stage, i) => (
                            <React.Fragment key={stage}>
                                <span className="px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 rounded font-mono uppercase">{stage}</span>
                                {i < (plugin.pipeline?.length ?? 0) - 1 && <span>→</span>}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Form */}
                <div className="p-6 space-y-6">
                    {schema.map(field => renderField(field))}

                    {error && (
                        <div className="text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2">
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitting || isGenerating}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-200 dark:disabled:bg-zinc-700 disabled:text-zinc-400 py-2.5 text-sm font-medium text-white transition-all"
                    >
                        {(isSubmitting || isGenerating)
                            ? <><Loader2 size={14} className="animate-spin" /> 流水线运行中...</>
                            : <><Sparkles size={14} /> 启动 AI 自动生成</>
                        }
                    </button>
                </div>
            </div>

            {lightboxSrc && (
                <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
            )}
        </>
    );
};
